import { IUploadFile } from "@/twapp/server/FileEndpoint";
import { IApiServer } from "../../../hooks/useTromanService";

export interface IFailedUpload {
    file: IUploadFile;
    error: any;
}

export interface IUploadState {
    active: IUploadFile;
    index: number;
    max: number;
    failed: IFailedUpload[];
}

export interface IUploadResult {
    successful: IUploadFile[];
    unsuccessful: IUploadFile[];
    failed: IFailedUpload[];
    aborted: boolean;
}

const abortedReason = Symbol("Uppladdning avbruten");

export class UploadManager {
    private uploading?: Promise<IUploadResult>;
    private controller: AbortController;
    
    constructor(
        private api: IApiServer,
        private files: IUploadFile[],
        private callback: (state: IUploadState) => void) {
        this.controller = new AbortController();
    }

    private invokeCallback(index: number, file: IUploadFile, failed: IFailedUpload[]) {
        try {
            this.callback({
                index: index + 1,
                max: this.files.length,
                active: file,
                failed,
            });
        } catch (error) {
            console.log('error while invoking callback', error);
        }
    }

    private async doUpload(): Promise<IUploadResult> {
        const signal = this.controller.signal;

        let successful: IUploadFile[] = [];
        let unsuccessful: IUploadFile[] = [];
        let failed: IFailedUpload[] = [];
        let aborted = false;

        for (let ii=0; ii<this.files.length; ++ii) {
            if (signal.aborted) {
                aborted = true;
                unsuccessful.push(...this.files.slice(ii));
                break;
            }
            
            const file = this.files[ii];
            this.invokeCallback(ii, file, failed);
            try {
                await this.api.Files.uploadFile(file, signal);
                successful.push(file);
            } catch (error) {
                unsuccessful.push(file);
                if (error !== abortedReason) {
                    failed = [...failed, { file, error }];
                }
            }
        }

        return { successful, unsuccessful, failed, aborted };
    }

    upload() {
        if (this.uploading == null) {
            this.uploading = this.doUpload();
        }
        return Promise.resolve(this.uploading);
    }

    abort() {
        this.controller.abort(abortedReason);
    }
}
